import BlockMedia, {
	type BlockMediaProductsDisplayType,
} from 'components/BlockMedia';
import { LayoutContainer } from 'components/Layout';
import RichText from 'components/RichText';
import { ScArrowLink, type ValidSitecoreLink } from 'components/SitecoreLink';
import Text from 'components/Text';
import type { GTMItemListId, GTMItemListName } from 'hooks/gtm';
import type { DigizuiteAsset } from 'models/asset';
import type { ProductCard } from 'models/productCard';
import type { Field, HeadingLevel } from 'models/sitecore';
import type { ColorString, HTMLString, JSONObject } from 'types';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';

export type VerticalAlign = 'Center' | 'End' | 'Start';
export interface BaseProps {
	backgroundColor?: ColorString;
	basePriceTextBackground?: ColorString;
	campaignBubbleLabel?: string;
	campaignBubbleSale?: boolean;
	campaignBubbleText?: string;
	className?: string;
	gtmItemListId: GTMItemListId;
	gtmItemListName: GTMItemListName;
	heading?: Field<string>;
	headingLevel?: HeadingLevel;
	id?: string;
	imageAlt: string | undefined;
	imageSrc: string;
	isMirrored?: boolean;
	isWide?: boolean;
	links?: ValidSitecoreLink[];
	preventAutoplay?: boolean;
	primaryLinkHref?: string;
	products?: ProductCard[];
	productsDisplayType: BlockMediaProductsDisplayType;
	productsRequestBody?: JSONObject;
	tallImageAlt: string | undefined;
	tallImageSrc: string | undefined;
	text?: Field<HTMLString>;
	verticalAlign?: VerticalAlign;
	video?: DigizuiteAsset;
}

interface PropsWithProducts extends BaseProps {
	products: ProductCard[] | undefined;
}
interface PropsWithRequestBody extends BaseProps {
	productsRequestBody: JSONObject | undefined;
}

export type Props = PropsWithProducts | PropsWithRequestBody;

export default function InspirationLandscape({
	backgroundColor,
	basePriceTextBackground,
	campaignBubbleLabel,
	campaignBubbleSale,
	campaignBubbleText,
	className,
	gtmItemListId,
	gtmItemListName,
	heading,
	headingLevel = 'h2',
	id,
	imageAlt,
	imageSrc,
	isMirrored,
	isWide,
	links,
	preventAutoplay,
	primaryLinkHref,
	products,
	productsDisplayType,
	productsRequestBody,
	tallImageAlt,
	tallImageSrc,
	text,
	verticalAlign = 'Start',
	video,
}: Props) {
	const isShopByMediaWithProducts =
		productsDisplayType === 'shopByMedia' && productsRequestBody;

	const media = (
		<BlockMedia
			className={cn(
				'col-span-full',
				isWide ? 'lg:col-span-8' : 'lg:col-span-6',
			)}
			href={isShopByMediaWithProducts ? undefined : primaryLinkHref}
			image={[
				{
					src: tallImageSrc ?? imageSrc,
					alt: tallImageAlt ?? imageAlt,
					variant: tallImageSrc ? 'landscape4x3Small' : 'landscape2x1Small',
					className: 'sm:hidden',
				},
				{
					src: imageSrc,
					alt: imageAlt,
					// Not using landscape2x1Half since the vertical stacking stays until
					// the lg breakpoint and the half size is too small for that.
					variant: 'landscape2x1',
					className: 'max-sm:hidden',
				},
			]}
			video={video}
			campaignBubbleSmallSize="small"
			campaignBubbleLargeSize="large"
			campaignBubbleLabel={campaignBubbleLabel}
			campaignBubbleSale={campaignBubbleSale}
			campaignBubbleText={campaignBubbleText}
			preventAutoplay={preventAutoplay}
			products={products}
			productsDisplayType={productsDisplayType}
			productsRequestBody={productsRequestBody}
			gtmItemListId={gtmItemListId}
			gtmItemListName={gtmItemListName}
			basePriceTextBackground={basePriceTextBackground}
			productAnimationPlacementClassName="absolute inset-y-[10%] right-10 flex max-h-64 justify-end sm:max-h-96 md:right-24 xl:inset-y-[15%]"
		/>
	);

	const renderSecondaryPart = (extraClassName?: string) => (
		<div
			className={cn(
				extraClassName,
				'col-span-full',
				isWide ? 'lg:col-span-4' : 'lg:col-span-6',
				// Padding to compensate for LayoutContainer's lack of sm gutters below.
				'max-sm:px-6',
				'max-lg:pt-3 lg:py-12',
				backgroundColor && [
					'max-lg:px-6 max-lg:pb-6',
					isMirrored ? 'lg:pl-12' : 'lg:pr-12',
				],
			)}
		>
			{heading && <Text as={headingLevel} field={heading} />}
			{text && <RichText className="mt-2" field={text} />}
			{is.arrayWithLength(links) && (
				<div
					className={cn(
						'flex flex-col items-start justify-start gap-4',
						text?.value && 'mt-4 lg:mt-6',
						!text?.value && 'mt-2 lg:mt-4',
					)}
				>
					{links.map((link) => (
						<ScArrowLink key={link.href} link={link} />
					))}
				</div>
			)}
		</div>
	);

	return (
		<LayoutContainer
			id={id}
			gutters="sm"
			outerClassName={className}
			withGrid
			innerBackgroundColor={backgroundColor}
			innerClassName={cn(
				verticalAlign === 'Start' && 'items-start',
				verticalAlign === 'Center' && 'items-center',
				verticalAlign === 'End' && 'items-end',
			)}
		>
			{/* The secondary text should always be below the primary part on small screens. */}
			{isMirrored && (
				<>
					{renderSecondaryPart('max-lg:hidden')}
					{media}
					{renderSecondaryPart('lg:hidden')}
				</>
			)}
			{!isMirrored && (
				<>
					{media}
					{renderSecondaryPart()}
				</>
			)}
		</LayoutContainer>
	);
}
InspirationLandscape.displayName = 'InspirationLandscape';
